<template>
  <div class="job-openings">
    <hero-section
      :title="jobOpening.name || 'Loading...'"
      :nav-indicator="navIndicator"
      :show-white-wave="false"
      :department="jobOpening.department ? [jobOpening.department] : []"
      :hard-skills="jobOpening.hardSkills || []"
      :job-details="{
        type: jobOpening.jobType?.name || '',
        workMode: jobOpening.workMode?.name || '',
        location: jobOpening.location || '',
      }"
      :has-apply-button="true"
      :apply-button-url="`/drop-your-info?jobId=${jobOpening.slug || ''}`"
      :apply-button-text="'Apply Now'"
    />
    <div class="section__container">
      <div class="loader" v-if="UIController.loader.contentLoading">
        <v-progress-circular indeterminate></v-progress-circular>
        <p class="mt-5">Hold On! while we fetch the available job openings.</p>
      </div>
      <div v-else>
        <v-card elevation="0" class="error-card" v-if="!jobOpening.slug">
          <v-card-title class="error-card__title text-left dms700"
            >This Job Opening is Not Available</v-card-title
          >
          <v-card-text>
            <h3 class="error-card__text-title text-left m500">
              Hmm! Looks like the job opening you are trying to access does not
              exist.
            </h3>
            <p class="error-card__text text-left m500">
              You can always look through other job openings available right
              now.
            </p>
          </v-card-text>
        </v-card>
        <div v-else class="job-post">
          <div v-if="jobOpening.roleSummary?.trim()" class="job-post-underline">
            <p class="job-post__sub-title role-summary dms600">Role Summary</p>
            <div
              class="job-post__description text-left prose"
              v-html="jobOpening.roleSummary" 
            ></div>
          </div>
          <div
            v-if="jobOpening.minimumQualifications?.trim()"
            class="job-post-underline"
          >
            <p class="job-post__sub-title dms600">Minimum Qualifications</p>
            <div
              class="job-post__description text-left prose"
              v-html="jobOpening.minimumQualifications" 
            ></div>
          </div>
          <div
            v-if="jobOpening.preferredQualifications?.trim()"
            class="job-post-underline"
          >
            <p class="job-post__sub-title dms600">Preferred Qualifications</p>
            <div
              class="job-post__description text-left prose"
              v-html="jobOpening.preferredQualifications" 
            ></div>
          </div>
          <div
            v-if="jobOpening.responsibilities?.trim()"
            class="job-post-underline"
          >
            <p class="job-post__sub-title dms600">Responsibilities</p>
            <div
              class="job-post__description text-left prose"
              v-html="jobOpening.responsibilities" 
            ></div>
          </div>
          <div v-if="jobOpening.benefits?.trim()" class="job-post-underline">
            <p class="job-post__sub-title dms600">Benefits</p>
            <div
              class="job-post__description text-left prose"
              v-html="jobOpening.benefits" 
            ></div>
          </div>
          <div v-if="jobOpening.footer?.trim()">
            <div
              class="job-post__footer text-left mt-8 prose"
              v-html="jobOpening.footer " 
            ></div>
          </div>
          <div>
            <div class="apply-container text-left">
              <v-btn
                class="job-opening__apply-button font-large"
                :href="`/drop-your-info?jobId=${jobOpening.slug}`"
                >Apply Now
                <!-- <v-icon size="x-small" class="ms-2"
                  >mdi-open-in-new</v-icon
                > -->
              </v-btn
              >
            </div>
            <div
              class="job-opening__contact-card"
              :style="{
                'border-top-color': jobOpening.jobContact?.color || '#000',
              }"
              v-if="hasJobContact"
            >
              <p class="job-post__sub-title text-left mb-2">Contact</p>
              <v-row class="contact-card">
                <v-col cols="4">
                  <v-img
                    v-if="jobOpening.jobContact?.profilePicture"
                    :src="jobOpening.jobContact.profilePicture"
                    eager
                    class="contact-card__img"
                  ></v-img>
                  <v-img
                    v-else-if="jobOpening.jobContact?.initials"
                    :src="`https://api.dicebear.com/6.x/initials/svg?seed=${jobOpening.jobContact.initials}`"
                    eager
                    class="contact-card__img"
                  ></v-img>
                </v-col>
                <v-col cols="8">
                  <p class="contact-card__name text-left">
                    <span class="contact-card__text">{{
                      jobOpening.jobContact?.username || ''
                    }}</span>
                  </p>
                  <p class="contact-card__mail text-left d-none d-md-block">
                    <a
                      :href="`mailto:${jobOpening.jobContact?.email || ''}`"
                      class="contact-card__link"
                      >{{ jobOpening.jobContact?.email || '' }}</a
                    >
                  </p>
                  <p class="contact-card__mail text-left d-md-none">
                    <v-btn
                      class="mt-3 contact__button m700"
                      :href="`mailto:${jobOpening.jobContact?.email || ''}`"
                      v-if="jobOpening.jobContact?.email"
                    >
                      Send Email
                    </v-btn>
                  </p>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeroSectionVue from '@/views/jobOpenings/HeroSection.vue';
import jobOpeningService from '@/services/API/jobOpeningService';

export default {
  name: 'JobOpening',
  components: {
    'hero-section': HeroSectionVue,
  },
  data: () => ({
    title: 'Loading...',
    showWhiteWave: true,
    navIndicator: [
      { text: 'Home', link: '/' },
      { text: 'Talents on Demand', link: '/talents-on-demand' },
      { text: '', link: '' },
    ],
    jobOpening: {
      id: null,
      name: null,
      roleSummary: null,
      minimumQualifications: null,
      responsibilities: null,
      benefits: null,
      location: null,
      department: null,
      hardSkills: null,
      jobContact: null,
      jobType: null,
      footer: null,
      preferredQualifications: null,
      type: null,
      workMode: null,
      slug: null,
    },
    UIController: {
      loader: {
        contentLoading: true,
      },
    },
  }),
  methods: {
    splitter(text) {
      return text ? text.split(/(?<!\\)\n/).filter((line) => line.trim()) : [];
    },
  },
  async created() {
    document.getElementById('loaderSection').classList.add('d-none');
    this.UIController.loader.contentLoading = true;
    try {
      const response = await jobOpeningService.showJobOpenings(
        this.$route.params.id
      );
      if (response.status === 200) {
        this.jobOpening.id = response.data.id || null;
        this.jobOpening.name = response.data.name || null;
        this.jobOpening.roleSummary = response.data.roleSummary || null;
        this.jobOpening.minimumQualifications = response.data.minimumQualifications || null;
        this.jobOpening.preferredQualifications =
          response.data.preferredQualifications || null;
        this.jobOpening.responsibilities =
          response.data.responsibilities || null;
        this.jobOpening.benefits = response.data.benefits || null;
        this.jobOpening.location = response.data.location || null;
        this.jobOpening.department = response.data.department || null;
        this.jobOpening.slug = response.data.slug || null;
        this.jobOpening.hardSkills = response.data.hardSkills || [];
        this.jobOpening.jobContact = response.data.jobContact[0] || null;
        this.jobOpening.jobType = response.data.jobType || null;
        this.jobOpening.footer = response.data.footer || null;
        this.jobOpening.type = response.data.type || null;
        this.jobOpening.workMode = response.data.workMode || null;
        this.navIndicator[2].text = this.jobOpening.name || '';
      }
    } catch (error) {
      console.error('Error fetching job opening:', error);
      this.jobOpening = {
        slug: null,
      };
    }
    this.title = this.jobOpening.name || 'Loading...';
    this.UIController.loader.contentLoading = false;
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  computed: {
    hasJobContact() {
      return !!this.jobOpening.jobContact?.username;
    },
  },
};
</script>

<style scoped>
.error-card {
  background-color: var(--light-1);
  max-width: 500px;
  margin-inline: auto;
  margin-block: 2rem;
}

.loader {
  padding-block: 5rem;
}
.job-openings {
  background-color: var(--light-1);
}

.section__container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  padding: 1rem 5rem;
  margin-left: auto;
  margin-right: auto;
  align-items: center;
}

.job-post {
  display: flex;
  flex-direction: column;
  justify-content: left;
  align-items: left;
  width: 100%;
  margin-block: 2rem;
}

.job-post__breadcrumbs {
  margin-bottom: 1rem;
  color: var(--gray-1);
}

.breadcrumb-item {
  margin-right: 0.5rem;
  text-decoration: none;
  color: var(--gray-1);
}

.breadcrumb-item.active {
  color: var(--dark-2);
}

.job-post__title {
  margin-block: 0.5rem;
  color: var(--dark-2);
  font-weight: bold;
}

.job-post__department-container {
  margin-bottom: 1rem;
}

.job-post__tags {
  margin-block: 1rem;
}

.job-post__skill-chip {
  margin-right: 0.5rem;
}

.job-post__details {
  margin-block: 1rem;
  display: flex;
  gap: 1rem;
  color: var(--gray-1);
}

.job-post__detail {
  display: flex;
  align-items: center;
}

.job-post__sub-title {
  text-align: left;
  /* font-size: rem; */
  font-size: clamp(24px, 5vw, 36px);
  line-height: 1.5rem;
  margin: 3rem 0 2rem 0rem;
  font-weight: 600;
}
.role-summary {
  margin-top: 1rem;
}
.job-post__description {
  line-height: 1.5rem;
  margin-bottom: 1.5rem;
  margin-top: 0.5rem;
  white-space: pre-line;
}
.job-post__footer {
  margin: 0;
}
.job-post-underline {
  border-bottom: 1px solid var(--gray-2);
}

.apply-container {
  margin: 3rem 0;
}
.job-opening__contact-title {
  color: black;
  font-weight: 800;
}
.job-opening__apply-button {
  background-color: #007ab2;
  color: #fff;
  font-size: 14px;
  font-weight: 800px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  text-transform: capitalize;
  cursor: pointer;
  height: 45px;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;
}
.job-opening__apply-button:hover {
  background-color: #00aeff;
  transform: scale(1.05);
}
.job-opening__contact-card {
  margin-top: 1rem;
}

.contact-card {
  margin-block: 1rem;
  padding: 1rem 0;
  border-radius: 0.8rem;
  display: flex;
  justify-content: left;
  align-items: center;
  align-content: left;
  background-color: #ffffff;
  /* box-shadow: 0px 2px 1px 4px snow; */
  max-width: 380px;
  margin: 2rem 0;
}
.contact-card__img {
  min-width: 70px;
  max-width: 100px;
  margin-inline: auto;
  border-radius: 50%;
}

.contact-card__link {
  text-decoration: underline;
  color: var(--dark-2);
}

.contact__button {
  background-color: #007ab2;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  margin-bottom: 20px;
  margin-top: 10px;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;
  text-transform: capitalize;
}
.contact__button:hover {
  background-color: #00aeff;
  transform: scale(1.05);
}

/* Override for centered alignment in JobOpeningsShow.vue */
.text__container {
  text-align: center;
  justify-content: center;
  align-items: center;
}

@media only screen and (max-width: 960px) {
  .section__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1100px;
    padding: 1rem 3rem;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
  }
  .job-post__sub-title {
    
    margin: 2rem 0 1rem 0rem;
    font-weight: 600;
  }
}
@media only screen and (max-width: 560px) {
  .section__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1100px;
    padding: 1rem 2rem;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
  }
}
</style>
